<div class="oy">
  <!-- Android only: the Play Store app, above the sticky header so it scrolls away with the page. -->
  <app-get-app-banner />
  <header class="oy-header">
    <div class="oy-header__inner">
      <div class="oy-header__left">
        <!-- Mobile only: opens the left slide-in nav drawer. -->
        <button type="button" class="oy-burger" (click)="toggleMenu()"
                [attr.aria-label]="i18n.t('nav.openMenu')" [attr.aria-expanded]="menuOpen()">☰</button>

        <!--
          A plain href, NOT routerLink, and that is deliberate: the logo is meant to reload the app
          every time it is tapped, including when the shopper is already on the landing page — where
          a routerLink to the current route does nothing at all. A real document load also replays
          the brand splash, which is the point.

          An anchor rather than a click handler calling location.reload(), so middle-click, ctrl-click
          and "open in new tab" all keep working, and the browser still shows the target on hover.

          Points at "/" rather than naming a page: the router's empty-path redirect decides what home
          is, so this link needs no edit the next time that changes.
        -->
        <a class="oy-logo" href="/" [attr.aria-label]="i18n.t('nav.home')">
          <img appBrandLogo="customer" [onDark]="true" />
        </a>

        <!-- Inline SVG rather than 📍/▾: the emoji pin renders as its own red-and-white glyph, which
             muddies the red bar and shifts shape per platform. `currentColor` keeps these on-brand. -->
        <button type="button" class="oy-loc" [class.is-empty]="!loc.location()"
                (click)="loc.openPicker(true)" aria-haspopup="dialog"
                [title]="loc.location()?.label || i18n.t('nav.setLocation')">
          <span class="oy-loc__pin" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 10c0 5.25-6.75 11-8 11s-8-5.75-8-11a8 8 0 0 1 16 0Z" />
              <circle cx="12" cy="10" r="2.75" />
            </svg>
          </span>
          <span class="oy-loc__text">
            <small>{{ loc.location() ? i18n.t('nav.deliverTo') : i18n.t('nav.shopNearest') }}</small>
            <strong>{{ loc.location()?.label || i18n.t('nav.setLocation') }}</strong>
          </span>
          <span class="oy-loc__chev" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </button>
      </div>

      <nav class="oy-nav">
        <!-- `display: contents` on desktop, so these sit in the header row exactly as before; on mobile the
             wrapper is hidden and the same destinations appear in the drawer instead. The bell and the cart
             stay OUTSIDE it — a shopper must always be one tap from their basket. -->
        <div class="oy-nav__items">
          @if (auth.isAuthenticated()) {
            <a class="oy-nav__link" routerLink="/orders" routerLinkActive="is-active">
              <span class="oy-nav__ico" aria-hidden="true">🧾</span><span class="oy-nav__label">{{ i18n.t('nav.orders') }}</span>
            </a>
            <a class="oy-nav__link" routerLink="/account" routerLinkActive="is-active">
              <span class="oy-nav__ico" aria-hidden="true">👤</span><span class="oy-nav__label">{{ accountLabel }}</span>
            </a>
          } @else {
            <button type="button" class="oy-btn oy-btn--outline" (click)="authModal.open(null, 'login')">{{ i18n.t('nav.login') }}</button>
            <button type="button" class="oy-btn oy-btn--primary" (click)="authModal.open(null, 'signup')">{{ i18n.t('nav.signupFree') }}</button>
          }
        </div>

        <!-- Language, beside Install and outside the signed-in check: a shopper who cannot read the
             English header is exactly who needs to reach this before doing anything else. -->
        <app-text-size-control variant="header" />
        <app-lang-switcher variant="header" />

        <!-- Desktop only (it hides itself under 860px, where the drawer copy takes over). Sits
             outside the signed-in check on purpose: a shopper browsing before they have an account
             is exactly who wants this on their home screen. -->
        <app-install-app variant="header" />

        @if (auth.isAuthenticated()) {
          <app-inbox-bell [base]="inboxBase" ordersRoute="/orders" />
        }

        <a class="oy-cart" routerLink="/cart" [attr.aria-label]="i18n.t('nav.cart')">
          <span class="oy-cart__icon" aria-hidden="true">🛒</span>
          <span class="oy-cart__label">{{ i18n.t('nav.cart') }}</span>
          @if (cart.count() > 0) { <span class="oy-cart__badge">{{ cart.count() }}</span> }
        </a>
      </nav>
    </div>

    <!-- Route progress: the delivery bike rides a track HANGING BELOW the header while a lazy page
         chunk loads. Inside the header so it can be positioned against it without hard-coding the
         header's height, and only in the DOM while a navigation is actually in flight — see
         navLoading for the anti-flicker timing. -->
    @if (navLoading()) {
      <app-bike-loader class="oy-navbar" variant="bar" />
    }
  </header>

  <!-- ── Mobile left slide-in nav drawer ── -->
  <div class="oy-drawer-backdrop" [class.is-open]="menuOpen()" (click)="closeMenu()"></div>
  <aside class="oy-drawer" [class.is-open]="menuOpen()" [attr.aria-label]="i18n.t('nav.menu')">
    <div class="oy-drawer__head">
      <span class="oy-drawer__brand">orderYaa</span>
      <button type="button" class="oy-drawer__close" (click)="closeMenu()" [attr.aria-label]="i18n.t('nav.closeMenu')">✕</button>
    </div>

    <nav class="oy-drawer__links" [attr.aria-label]="i18n.t('nav.customerNav')">
      <!-- The landing design first, under the generic "Shops" label — this is where an empty path
           lands, so it is the entry that must carry the plain browse wording and light up on home.
           The classic list follows it as a full peer, labelled like the in-page view toggle. -->
      <a routerLink="/discover" routerLinkActive="is-active" (click)="closeMenu()">
        <span aria-hidden="true">🏬</span> {{ i18n.t('nav.shops') }}
      </a>
      <a routerLink="/stores" routerLinkActive="is-active" (click)="closeMenu()">
        <span aria-hidden="true">☰</span> {{ i18n.t('discover.listView') }}
      </a>
      <a routerLink="/cart" routerLinkActive="is-active" (click)="closeMenu()">
        <span aria-hidden="true">🛒</span> {{ i18n.t('nav.cart') }}
        @if (cart.count() > 0) { <span class="oy-drawer__count">{{ cart.count() }}</span> }
      </a>
      @if (auth.isAuthenticated()) {
        <a routerLink="/orders" routerLinkActive="is-active" (click)="closeMenu()">
          <span aria-hidden="true">🧾</span> {{ i18n.t('nav.myOrders') }}
        </a>
        <a routerLink="/favourites" routerLinkActive="is-active" (click)="closeMenu()">
          <span aria-hidden="true">♥</span> {{ i18n.t('nav.myFavourites') }}
        </a>
        <a routerLink="/transactions" routerLinkActive="is-active" (click)="closeMenu()">
          <span aria-hidden="true">📒</span> {{ i18n.t('nav.myTransactions') }}
        </a>
        <a routerLink="/rewards" routerLinkActive="is-active" (click)="closeMenu()">
          <span aria-hidden="true">🎁</span> {{ i18n.t('nav.myRewards') }}
        </a>
        <a routerLink="/account" routerLinkActive="is-active" (click)="closeMenu()">
          <span aria-hidden="true">👤</span> {{ accountLabel }}
        </a>
      }
      <!-- For guests too: the page signs them in first and brings them back to it. -->
      <a routerLink="/feedback" [queryParams]="{ from: feedbackFrom }" routerLinkActive="is-active" (click)="closeMenu()">
        <span aria-hidden="true">💬</span> {{ i18n.t('nav.sendFeedback') }}
      </a>

      <!-- The mobile route to the language toggle — the header pill hides at this width. -->
      <app-text-size-control variant="drawer" />
      <app-lang-switcher variant="drawer" (changed)="closeMenu()" />
      <!-- The mobile route to installing — the header pill hides at this width. Renders nothing
           once the app is installed, or on a browser that cannot install. -->
      <app-install-app variant="drawer" (activated)="closeMenu()" />
    </nav>

    @if (auth.isAuthenticated()) {
      <button type="button" class="oy-drawer__out" (click)="logout()">{{ i18n.t('nav.signOut') }}</button>
    } @else {
      <!-- Drawer-specific styling on purpose: .oy-btn--outline/--primary are tuned for the RED header
           (white text, translucent white fill), which is invisible on this white panel. -->
      <div class="oy-drawer__auth">
        <button type="button" class="oy-drawer__btn" (click)="openAuth('login')">{{ i18n.t('nav.login') }}</button>
        <button type="button" class="oy-drawer__btn oy-drawer__btn--solid" (click)="openAuth('signup')">{{ i18n.t('nav.signup') }}</button>
      </div>
    }
  </aside>

  <main class="oy-main">
    <!-- First-visit install prompt, above the page content. In the flow rather than floating, so it
         pushes the page down instead of covering a shop card, and it takes itself out of the DOM for
         good once dismissed. The header's Install button is unaffected and stays available. -->
    <app-install-prompt />
    <router-outlet></router-outlet>
  </main>

  <footer class="oy-footer">
    <div class="oy-footer__inner">
      <img class="oy-footer__logo" appBrandLogo="customer" [onDark]="true" loading="lazy" />
      <p class="oy-footer__tag">{{ i18n.t('nav.tagline') }}</p>
      <nav class="oy-footer__links">
        <a routerLink="/discover">{{ i18n.t('nav.shops') }}</a>
        <a routerLink="/services">{{ i18n.t('nav.services') }}</a>
        <a routerLink="/bookings">{{ i18n.t('nav.bookings') }}</a>
        <a routerLink="/cart">{{ i18n.t('nav.cart') }}</a>
      </nav>
      <p class="oy-footer__copy">{{ i18n.t('nav.copyright', { year: year }) }}</p>
    </div>
  </footer>

  <!--
    ── "Your rider is outside", on EVERY page ──
    A customer waiting on food browses other shops or puts the phone down; the tracking screen is where
    they look least. This banner is the app-wide surface for the alarm, so the alert reaches them
    wherever they are. Hidden when they're already on that order's tracking page — its panel says more.
  -->
  @if (showArrivalBanner) {
    @if (arrival.alert(); as ao) {
      <div class="oy-arrival" role="alertdialog" aria-live="assertive">
        <span class="oy-arrival__icon" aria-hidden="true">🛵</span>
        <div class="oy-arrival__body">
          <strong class="oy-arrival__title">
            @if (arrival.distanceLabel(ao); as away) {
              {{ i18n.t('headout.near', { name: ao.riderName || i18n.t('track.yourRider'), away: away }) }}
            } @else {
              {{ i18n.t('track.riderOnWay', { name: ao.riderName || i18n.t('track.yourRider') }) }}
            }
          </strong>
          <a class="oy-arrival__link" [routerLink]="['/order', ao.orderId]">{{ i18n.t('headout.viewOrder') }}</a>
        </div>
        <button type="button" class="oy-arrival__cta" [disabled]="arrival.busy()"
                (click)="arrival.announce(ao.orderId)">
          @if (arrival.busy()) {
            {{ i18n.t('headout.telling') }}
          } @else {
            {{ i18n.t('headout.button') }}
            @if ((ao.amountToCollect ?? 0) > 0) {
              <span class="oy-arrival__cash">
                {{ i18n.t('headout.withCash', { currency: ao.currencyCode || '', amount: (ao.amountToCollect | number:'1.0-0') || '' }) }}
              </span>
            }
          }
        </button>
        <button type="button" class="oy-arrival__mute" (click)="arrival.mute(ao.orderId)"
                [attr.aria-label]="i18n.t('headout.mute')">🔇</button>
      </div>
    }
  }

  <!-- Rendered HERE, at the layout root, and nowhere else: it is a fixed full-screen overlay, and
       both places the install button sits are stacking contexts (the sticky header, and the drawer's
       transform — which would also become this overlay's containing block). -->
  <app-install-help-sheet />

  <!-- Phone-only tab bar. Renders nothing above 640px; see BottomNavComponent for why it coexists
       with the drawer rather than replacing it. -->
  <app-bottom-nav />

  <app-location-modal></app-location-modal>
  <app-auth-modal></app-auth-modal>
  <!-- "Download our app now!" — waits until neither modal above, nor the drawer, is open. -->
  <app-get-app-modal [blocked]="menuOpen()" />
</div>
